import { useMemo, useState } from 'react';

import { NewPriceVersionDialog } from './new-price-version-dialog';
import { PriceHistorySheet } from './price-history-sheet';
import type { LlmPriceVersionDto } from '../../../../../../backend/src/llm/pricing/dto/llm-pricing.response.dto';
import { useFetchLlmPrices } from '@/api/queries/analyticsQueries/useFetchLlmPrices';
import { useGlobalAdminCheck } from '@/hooks/useGlobalAdminCheck';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/new/components/shadcn/table';
import { SubTitle } from '@/new/components/subtitle';

/** Trims the trailing zeroes a numeric(12,6) round-trip leaves behind. */
const formatRate = (value: number) => `$${Number(value.toFixed(6))}`;

/** Strips the shared `anthropic.` prefix so the model column stays scannable. */
const shortModelName = (model: string) => model.replace(/^anthropic\./, '');

const RATE_COLUMNS = [
  { key: 'inputUsdPerMTok', label: 'Input' },
  { key: 'cacheReadUsdPerMTok', label: 'Cache read' },
  { key: 'cacheWriteUsdPerMTok', label: 'Cache write' },
  { key: 'outputUsdPerMTok', label: 'Output' },
] as const;

export function LlmPricing() {
  useGlobalAdminCheck();

  const { data: pricesData, isLoading: isPricesLoading, isError } = useFetchLlmPrices();

  const [historyModel, setHistoryModel] = useState<string | null>(null);

  const geos = useMemo(
    () => [...new Set(pricesData?.current.map((price) => price.servingGeo) ?? [])].sort(),
    [pricesData],
  );

  // Grouped so the matrix reads model-per-row, geo-per-column — the shape that
  // makes an accidental divergence between geos obvious at a glance.
  const byModel = useMemo(() => {
    const grouped = new Map<string, Map<string, LlmPriceVersionDto>>();
    for (const price of pricesData?.current ?? []) {
      const forModel = grouped.get(price.model) ?? new Map<string, LlmPriceVersionDto>();
      forModel.set(price.servingGeo, price);
      grouped.set(price.model, forModel);
    }
    return [...grouped.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [pricesData]);

  const gaps = pricesData?.gaps ?? [];
  const knownModels = useMemo(
    () =>
      [
        ...new Set([
          ...(pricesData?.current.map(({ model }) => model) ?? []),
          ...(pricesData?.history.map(({ model }) => model) ?? []),
          ...(pricesData?.gaps.map(({ model }) => model) ?? []),
        ]),
      ].sort(),
    [pricesData],
  );

  return (
    <>
      <Breadcrumb
        items={[
          { href: '/admin/organisations', label: 'Admin', active: false },
          { href: '/admin/llm-pricing', label: 'LLM Pricing', active: true },
        ]}
      />
      <PageContainer className="mb-24">
        <PageHeader>
          <PageTitle
            text="LLM Pricing"
            subtext="Rates Kim costs each turn against. Versions are append-only — a change or a correction adds a new version
          rather than editing an old one, so historical spend always stays explainable by the version it was billed
          under."
          />

          <NewPriceVersionDialog knownModels={knownModels} />
        </PageHeader>

        {isError && (
          <div className="mt-7 max-w-3xl rounded-md border border-red-300 bg-red-50 p-4">
            <div className="flex items-center gap-2 font-medium text-red-900">
              <Icon className="lucide--circle-alert text-base" />
              <span>Could not load prices</span>
            </div>
            <p className="mt-1 text-red-800">
              Nothing below is trustworthy — an empty rate matrix here does not mean no prices are configured, and a
              missing warning does not mean every model is priced. Reload to try again.
            </p>
          </div>
        )}

        {gaps.length > 0 && (
          <div className="mt-7 max-w-3xl rounded-md border border-amber-300 bg-amber-50 p-4">
            <div className="flex items-center gap-2 font-medium text-amber-900">
              <Icon className="lucide--triangle-alert text-base" />
              <span>{gaps.length === 1 ? '1 model is' : `${gaps.length} models are`} billing uncosted</span>
            </div>
            <p className="mt-1 text-amber-800">
              Kim has served these but no price version covers them, so their spend is recorded with no cost or credits.
              Add a price version for each.
            </p>
            <ul className="mt-3 space-y-1">
              {gaps.map((gap) => (
                <li key={`${gap.model} ${gap.servingGeo}`} className="text-amber-900">
                  <span className="font-mono">{shortModelName(gap.model)}</span> in{' '}
                  <span className="font-mono">{gap.servingGeo}</span> —{' '}
                  {gap.usageRowCount === 1 ? '1 turn' : `${gap.usageRowCount} turns`} uncosted
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-6">
          <div className="px-1">
            <SubTitle text="Current rates" />

            <p className="mt-1 mb-4 text-gray-500">
              USD per million tokens, by model and Bedrock cross-region inference geo.
            </p>
          </div>

          {isPricesLoading ? (
            <Skeleton className="h-64 w-full max-w-5xl" />
          ) : isError ? (
            <p className="text-gray-500">Rates unavailable — see the error above.</p>
          ) : byModel.length === 0 ? (
            <p className="text-gray-500">No price versions yet.</p>
          ) : (
            <div className="w-fit overflow-x-auto rounded-md border border-gray-200">
              <Table className="border-separate border-spacing-0">
                <TableHeader className="bg-gray-50 [&_th]:h-auto [&_th]:px-3 [&_th]:py-2.25 [&_tr]:border-0">
                  <TableRow>
                    <TableHead className="whitespace-nowrap">Model</TableHead>
                    <TableHead className="border-l border-gray-200 whitespace-nowrap">Rate</TableHead>
                    {geos.map((geo) => (
                      <TableHead key={geo} className="border-l border-gray-200 font-mono whitespace-nowrap">
                        {geo}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody className="[&_td]:border-t [&_td]:border-gray-200 [&_td]:px-3 [&_td]:py-2.25 [&_tr]:border-0">
                  {byModel.map(([model, byGeo]) =>
                    RATE_COLUMNS.map((rate, rateIndex) => (
                      <TableRow key={`${model} ${rate.key}`}>
                        {rateIndex === 0 && (
                          <TableCell
                            rowSpan={RATE_COLUMNS.length}
                            className="relative align-top font-mono whitespace-nowrap"
                          >
                            <span>{shortModelName(model)}</span>
                            <Button
                              size="sm"
                              className="absolute right-2.25 bottom-2.25"
                              onClick={() => setHistoryModel(model)}
                            >
                              <Icon className="lucide--list" />
                              See full price history
                            </Button>
                          </TableCell>
                        )}
                        <TableCell className="border-l border-gray-200 whitespace-nowrap text-gray-500">
                          {rate.label}
                        </TableCell>
                        {geos.map((geo) => {
                          const price = byGeo.get(geo);
                          return (
                            <TableCell key={geo} className="border-l border-gray-200 whitespace-nowrap tabular-nums">
                              {price ? formatRate(price[rate.key]) : <span className="text-gray-400">—</span>}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    )),
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </PageContainer>

      <PriceHistorySheet
        model={historyModel}
        onClose={() => setHistoryModel(null)}
        history={pricesData?.history ?? []}
      />
    </>
  );
}
